import type { AnalyticsIdentity } from '../domain/analyticsEvent';

const ANONYMOUS_ID_STORAGE_KEY = 'architrave-analytics-anonymous-id-v1';
const SESSION_ID_STORAGE_KEY = 'architrave-analytics-session-id-v1';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let inMemoryAnonymousId: string | null = null;
let inMemorySessionId: string | null = null;

const bytesToUuid = (bytes: Uint8Array): string => {
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
};

export const createAnalyticsEventId = (): string => {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return bytesToUuid(bytes);
};

const getOrCreateStoredId = (
  storage: Storage,
  key: string,
): string => {
  const stored = storage.getItem(key);
  if (stored && UUID_PATTERN.test(stored)) {
    return stored;
  }

  const created = createAnalyticsEventId();
  storage.setItem(key, created);
  return created;
};

const getAnonymousId = (): string => {
  try {
    return getOrCreateStoredId(window.localStorage, ANONYMOUS_ID_STORAGE_KEY);
  } catch {
    inMemoryAnonymousId ??= createAnalyticsEventId();
    return inMemoryAnonymousId;
  }
};

const getSessionId = (): string => {
  try {
    return getOrCreateStoredId(window.sessionStorage, SESSION_ID_STORAGE_KEY);
  } catch {
    inMemorySessionId ??= createAnalyticsEventId();
    return inMemorySessionId;
  }
};

export const getAnalyticsIdentity = (): AnalyticsIdentity => ({
  anonymousId: getAnonymousId(),
  sessionId: getSessionId(),
});
